import React, { useState, useEffect, memo } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Save, Heart, Flame, Activity } from 'lucide-react';
import { toast } from 'sonner';

const SettingsHormonalHealth = memo(function SettingsHormonalHealth() {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();

  const [hormonalForm, setHormonalForm] = useState({
    biological_sex: user?.biological_sex || '',
    life_phase: user?.life_phase || '',
    cycle_duration: user?.cycle_duration || 28,
    last_period_date: user?.last_period_date || '',
    energy_recovery_level: user?.energy_recovery_level || 7
  });
  const [hormonalSaving, setHormonalSaving] = useState(false);
  const [cycleInfo, setCycleInfo] = useState(null);

  useEffect(() => {
    if (user?.biological_sex === 'femme' && user?.life_phase === 'cycle_actif') {
      api.get('/users/cycle-info').then(res => {
        if (res.data.active) setCycleInfo(res.data);
      }).catch(() => {});
    }
  }, [user?.biological_sex, user?.life_phase, user?.last_period_date]);

  const saveHormonalProfile = async () => {
    setHormonalSaving(true);
    try {
      const data = { ...hormonalForm };
      if (data.biological_sex !== 'femme') {
        delete data.life_phase;
        delete data.cycle_duration;
        delete data.last_period_date;
      }
      if (data.biological_sex !== 'homme') {
        delete data.energy_recovery_level;
      }
      await api.put('/users/profile', data);
      updateUser({ ...user, ...data });
      toast.success(language === 'fr' ? 'Profil hormonal mis a jour!' : 'Hormonal profile updated!');
      if (data.biological_sex === 'femme' && data.life_phase === 'cycle_actif' && data.last_period_date) {
        api.get('/users/cycle-info').then(res => {
          if (res.data.active) setCycleInfo(res.data);
        }).catch(() => {});
      }
    } catch {
      toast.error(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving');
    } finally {
      setHormonalSaving(false);
    }
  };

  return (
    <div>
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-400" />
            {language === 'fr' ? 'Santé Hormonale' : 'Hormonal Health'}
          </CardTitle>
          <CardDescription>
            {language === 'fr'
              ? 'Le Coach YUMIX adapté ses conseils selon ton profil biologique'
              : 'Coach YUMIX adapts advice based on your biological profile'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Biological Sex */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {language === 'fr' ? 'Sexe biologique' : 'Biological sex'}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'femme', label: language === 'fr' ? 'Femme' : 'Female', icon: '\u2640' },
                { value: 'homme', label: language === 'fr' ? 'Homme' : 'Male', icon: '\u2642' }
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setHormonalForm(prev => ({ ...prev, biological_sex: opt.value, life_phase: '' }))}
                  className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                    hormonalForm.biological_sex === opt.value
                      ? 'border-pink-500 bg-pink-500/10 text-pink-400'
                      : 'border-border bg-secondary text-muted-foreground hover:border-pink-500/30'
                  }`}
                  data-testid={`hormonal-sex-${opt.value}`}
                >
                  {opt.icon} {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Female-specific: Life Phase */}
          {hormonalForm.biological_sex === 'femme' && (
            <div className="space-y-3 p-4 rounded-lg bg-pink-500/5 border border-pink-500/20">
              <label className="text-sm font-medium text-pink-300">
                {language === 'fr' ? 'Phase de vie' : 'Life phase'}
              </label>
              <div className="grid grid-cols-1 gap-2">
                {[
                  { value: 'cycle_actif', label: language === 'fr' ? 'Cycle menstruel actif' : 'Active menstrual cycle' },
                  { value: 'perimenopause', label: language === 'fr' ? 'Perimenopause' : 'Perimenopause' },
                  { value: 'menopause', label: language === 'fr' ? 'Menopause' : 'Menopause' }
                ].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setHormonalForm(prev => ({ ...prev, life_phase: opt.value }))}
                    className={`p-2.5 rounded-lg border text-sm text-left transition-all ${
                      hormonalForm.life_phase === opt.value
                        ? 'border-pink-500 bg-pink-500/10 text-pink-300'
                        : 'border-border bg-secondary text-muted-foreground hover:border-pink-500/30'
                    }`}
                    data-testid={`hormonal-phase-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {hormonalForm.life_phase === 'cycle_actif' && (
                <div className="space-y-4 pt-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-pink-300">
                      {language === 'fr' ? 'Duree du cycle' : 'Cycle duration'}
                    </label>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">21j</span>
                      <input type="range" min={21} max={35} value={hormonalForm.cycle_duration}
                        onChange={(e) => setHormonalForm(prev => ({ ...prev, cycle_duration: parseInt(e.target.value) }))}
                        className="flex-1 accent-pink-500" data-testid="hormonal-cycle-duration" />
                      <span className="text-xs text-muted-foreground">35j</span>
                      <span className="text-lg font-bold text-pink-400 w-12 text-center">{hormonalForm.cycle_duration}j</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-pink-300">
                      {language === 'fr' ? 'Date de tes dernieres règles' : 'Date of your last period'}
                    </label>
                    <Input type="date" value={hormonalForm.last_period_date}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setHormonalForm(prev => ({ ...prev, last_period_date: e.target.value }))}
                      className="bg-secondary border-border" data-testid="hormonal-last-period-date" />
                    <p className="text-xs text-muted-foreground italic">
                      {language === 'fr'
                        ? 'Permet au Coach YUMIX d\'adapter tes calories et entraînements selon ta phase.'
                        : 'Allows Coach YUMIX to adapt your calories and training based on your phase.'}
                    </p>
                  </div>

                  {/* Cycle Phase Indicator */}
                  {cycleInfo && cycleInfo.active && (
                    <div className="p-3 rounded-lg bg-pink-500/10 border border-pink-500/30 space-y-2.5" data-testid="cycle-phase-indicator">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-pink-300">
                          {language === 'fr' ? cycleInfo.phase_label_fr : cycleInfo.phase_label_en}
                        </span>
                        <span className="text-xs text-pink-400/70">
                          {language === 'fr' ? `Jour ${cycleInfo.cycle_day}/${cycleInfo.cycle_duration}` : `Day ${cycleInfo.cycle_day}/${cycleInfo.cycle_duration}`}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {language === 'fr' ? cycleInfo.phase_desc_fr : cycleInfo.phase_desc_en}
                      </p>
                      {cycleInfo.nutrition?.calorie_bonus > 0 && (
                        <div className="space-y-1.5 p-2 rounded bg-orange-500/10 border border-orange-500/20">
                          <div className="flex items-center gap-2 text-xs">
                            <Flame className="w-3.5 h-3.5 text-orange-400" />
                            <span className="font-semibold text-orange-300">
                              {language === 'fr' ? cycleInfo.nutrition.note_fr : cycleInfo.nutrition.note_en}
                            </span>
                          </div>
                          {cycleInfo.nutrition.focus_fr && (
                            <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 text-[10px] font-bold uppercase tracking-wide" data-testid="nutrition-focus-badge">
                              {language === 'fr' ? cycleInfo.nutrition.focus_fr : cycleInfo.nutrition.focus_en}
                            </span>
                          )}
                          <p className="text-[11px] text-muted-foreground leading-relaxed">
                            {language === 'fr' ? cycleInfo.nutrition.macro_tips_fr : cycleInfo.nutrition.macro_tips_en}
                          </p>
                        </div>
                      )}
                      {cycleInfo.training?.intensity_modifier < 1.0 && (
                        <div className="space-y-1 p-2 rounded bg-yellow-500/10 border border-yellow-500/20">
                          <div className="flex items-center gap-2 text-xs">
                            <Activity className="w-3.5 h-3.5 text-yellow-400" />
                            <span className="font-semibold text-yellow-300">
                              {language === 'fr' ? cycleInfo.training.label_fr : cycleInfo.training.label_en}
                            </span>
                          </div>
                          <ul className="text-[11px] text-muted-foreground space-y-0.5 ml-5 list-disc">
                            {(language === 'fr' ? cycleInfo.training.recommendations_fr : cycleInfo.training.recommendations_en)?.slice(0, 2).map((rec, i) => (
                              <li key={i}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p className="text-xs text-pink-400/60">
                        {language === 'fr'
                          ? `Prochaines regles dans ~${cycleInfo.days_until_period} jours`
                          : `Next period in ~${cycleInfo.days_until_period} days`}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Male-specific: Energy/Recovery */}
          {hormonalForm.biological_sex === 'homme' && (
            <div className="space-y-3 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <label className="text-sm font-medium text-blue-300">
                {language === 'fr' ? 'Énergie / Recuperation globale' : 'Energy / Recovery level'}
              </label>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">1</span>
                <input type="range" min={1} max={10} value={hormonalForm.energy_recovery_level}
                  onChange={(e) => setHormonalForm(prev => ({ ...prev, energy_recovery_level: parseInt(e.target.value) }))}
                  className="flex-1 accent-blue-500" data-testid="hormonal-energy-slider" />
                <span className="text-xs text-muted-foreground">10</span>
                <span className="text-lg font-bold text-blue-400 w-8 text-center">{hormonalForm.energy_recovery_level}</span>
              </div>
              <p className="text-xs text-muted-foreground italic">
                {hormonalForm.energy_recovery_level <= 4
                  ? (language === 'fr' ? 'Le Coach ajustera pour favoriser la recuperation' : 'Coach will adjust for recovery')
                  : hormonalForm.energy_recovery_level >= 8
                    ? (language === 'fr' ? 'Pleine énergie ! Le Coach peut pousser l\'intensite' : 'Full energy! Coach can push intensity')
                    : (language === 'fr' ? 'Niveau modere - equilibre entre effort et repos' : 'Moderate level - balance effort and rest')}
              </p>
            </div>
          )}

          <Button onClick={saveHormonalProfile} disabled={hormonalSaving || !hormonalForm.biological_sex}
            className="bg-pink-500/80 hover:bg-pink-500 text-white w-full" data-testid="save-hormonal-btn">
            {hormonalSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {language === 'fr' ? 'Sauvegarder le profil hormonal' : 'Save hormonal profile'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
});

export default SettingsHormonalHealth;
